/**
 * Dr. Volos — penza-cosmetology theme entry (ESM).
 * Base UI + dynamic motion bundles.
 */

import { afterLcp } from './after-lcp.js';

const cfg = window.penzaTheme || {};
const themeDist = `${(cfg.themeUri || '').replace(/\/$/, '')}/assets/dist`;

const BOOKING_CLICK_SEL =
  '[data-open-booking], [data-booking-open], .btn-book, [data-booking], .header-cta-book';

let bookingModule = null;
let bookingInited = false;
let fortuneBooted = false;
const COOKIE_KEY = cfg.cookieKey || 'penza_cookie_consent';
const COOKIE_DAYS = 365;

let motionCleanup = null;

function ready(fn) {
  if (document.readyState !== 'loading') fn();
  else document.addEventListener('DOMContentLoaded', fn);
}

function setCookie(name, value, days) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
  try {
    localStorage.setItem(name, value);
  } catch {
    /* noop */
  }
}

function getConsent() {
  try {
    if (localStorage.getItem(COOKIE_KEY) === 'accepted') return true;
  } catch {
    /* noop */
  }
  return document.cookie.includes(`${COOKIE_KEY}=accepted`);
}

function initCookieBanner() {
  const banner = document.getElementById('cookie-banner');
  const acceptBtn = document.querySelector('[data-cookie-accept]');
  if (!banner || !acceptBtn) return;

  if (getConsent()) {
    banner.setAttribute('hidden', '');
    return;
  }

  banner.removeAttribute('hidden');
  acceptBtn.addEventListener('click', () => {
    setCookie(COOKIE_KEY, 'accepted', COOKIE_DAYS);
    banner.setAttribute('hidden', '');
  });
}

function closeMobileNav() {
  const toggle = document.querySelector('[data-nav-toggle]');
  const panel = document.querySelector('[data-nav-panel]');
  if (!toggle || !panel) return;
  panel.classList.remove('is-open');
  toggle.classList.remove('is-active');
  toggle.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('is-mobile-nav-open');
}

function initMobileNav() {
  const toggle = document.querySelector('[data-nav-toggle]');
  const panel = document.querySelector('[data-nav-panel]');
  if (!toggle || !panel) return;

  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const open = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    toggle.classList.toggle('is-active', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('is-mobile-nav-open', open);
    if (!open) {
      panel.querySelectorAll('[data-nav-mega].is-open, [data-nav-dropdown].is-open').forEach((item) => {
        item.classList.remove('is-open');
        const btn = item.querySelector('.primary-menu__trigger');
        const sub = item.querySelector('.nav-mega, .nav-dropdown');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (sub) sub.setAttribute('hidden', '');
      });
    }
  });

  document.addEventListener('click', (e) => {
    if (!panel.classList.contains('is-open')) return;
    if (!panel.contains(e.target) && !toggle.contains(e.target)) {
      closeMobileNav();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel.classList.contains('is-open')) {
      closeMobileNav();
    }
  });
}

function initMegaNav() {
  const megaItems = document.querySelectorAll('[data-nav-mega], [data-nav-dropdown]');
  if (!megaItems.length) return;

  const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

  const closeAll = (except) => {
    megaItems.forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      const btn = item.querySelector('.primary-menu__trigger');
      const panel = item.querySelector('.nav-mega, .nav-dropdown');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (panel) panel.setAttribute('hidden', '');
    });
  };

  const hoverCloseTimers = new WeakMap();

  const clearHoverClose = (item) => {
    const timer = hoverCloseTimers.get(item);
    if (timer) {
      clearTimeout(timer);
      hoverCloseTimers.delete(item);
    }
  };

  const scheduleHoverClose = (item, btn, panel) => {
    clearHoverClose(item);
    hoverCloseTimers.set(
      item,
      setTimeout(() => {
        item.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        panel.setAttribute('hidden', '');
        hoverCloseTimers.delete(item);
      }, 150)
    );
  };

  megaItems.forEach((item) => {
    const btn = item.querySelector('.primary-menu__trigger');
    const panel = item.querySelector('.nav-mega, .nav-dropdown');
    if (!btn || !panel) return;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const willOpen = !item.classList.contains('is-open');
      closeAll(willOpen ? item : null);
      item.classList.toggle('is-open', willOpen);
      btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      panel.toggleAttribute('hidden', !willOpen);
    });

    item.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;
      clearHoverClose(item);
      closeAll(item);
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
    });

    item.addEventListener('mouseleave', () => {
      if (!isDesktop()) return;
      scheduleHoverClose(item, btn, panel);
    });

    panel.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;
      clearHoverClose(item);
    });
  });

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nav-mega], [data-nav-dropdown]')) return;
    closeAll();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });
}

function syncHeaderOffset() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const height = Math.ceil(header.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--header-offset', `${height}px`);
}

function initStickyHeader() {
  const header = document.querySelector('[data-motion="header-sticky"], .site-header');
  if (!header) return;

  let ticking = false;
  const update = () => {
    const scrolled = window.scrollY > 24;
    syncHeaderOffset();
    header.classList.toggle('is-scrolled', scrolled);
    ticking = false;
  };

  const schedule = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  if (document.fonts?.ready) {
    document.fonts.ready.then(schedule);
  }
  schedule();
}

function trackClicks() {
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-goal]');
    if (!el) return;
    const goal = el.getAttribute('data-goal');
    document.dispatchEvent(new CustomEvent('penza:goal', { detail: { goal, element: el } }));
    if (typeof window.ym === 'function' && cfg.metrikaId) {
      window.ym(cfg.metrikaId, 'reachGoal', goal);
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', goal, { event_category: 'cta' });
    }
  });
}

function formatRuPhone(value) {
  const digits = String(value).replace(/\D/g, '');
  let normalized = digits;
  if (!normalized) return '';
  if (normalized.startsWith('8')) normalized = `7${normalized.slice(1)}`;
  if (!normalized.startsWith('7')) normalized = `7${normalized}`;
  normalized = normalized.slice(0, 11);
  const parts = [
    normalized.slice(1, 4),
    normalized.slice(4, 7),
    normalized.slice(7, 9),
    normalized.slice(9, 11),
  ];
  let out = '+7';
  if (parts[0]) out += ` (${parts[0]}`;
  if (parts[0] && parts[0].length === 3) out += ')';
  if (parts[1]) out += ` ${parts[1]}`;
  if (parts[2]) out += `-${parts[2]}`;
  if (parts[3]) out += `-${parts[3]}`;
  return out;
}

function initPhoneMasks() {
  document.querySelectorAll('[data-phone-mask]').forEach((input) => {
    input.addEventListener('input', () => {
      const formatted = formatRuPhone(input.value);
      input.value = formatted;
    });
    input.addEventListener('focus', () => {
      if (!input.value) input.value = '+7 ';
    });
    input.addEventListener('blur', () => {
      if (input.value === '+7 ' || input.value === '+7') input.value = '';
    });
  });
}

function syncConsentSubmit(form) {
  const checkbox = form.querySelector('[data-consent-checkbox]');
  const submit = form.querySelector('[data-consent-submit]');
  if (!checkbox || !submit) return;
  submit.disabled = !checkbox.checked;
}

function initConsentGates() {
  document.querySelectorAll('[data-lead-form]').forEach((form) => {
    const checkbox = form.querySelector('[data-consent-checkbox]');
    if (!checkbox) return;
    syncConsentSubmit(form);
    checkbox.addEventListener('change', () => syncConsentSubmit(form));
  });
}

function syncLeadFormContext(form) {
  const pageUrlInput = form.querySelector('[data-page-url]');
  if (pageUrlInput) {
    pageUrlInput.value = window.location.href;
  }

  const typeInput = form.querySelector('[data-lead-type]');
  const serviceInput = form.querySelector('[name="lead_service"]');
  const serviceSlug = serviceInput?.value?.trim() || '';
  if (typeInput && serviceSlug) {
    const baseType = typeInput.dataset.baseType || typeInput.value || 'Модалка записи';
    if (!typeInput.dataset.baseType) {
      typeInput.dataset.baseType = baseType;
    }
    if (baseType.includes('Модалка') || baseType.includes('Колесо')) {
      typeInput.value = `${baseType} — ${serviceSlug}`;
    }
  }
}

/**
 * Fortune wheel / external widgets: submit lead with custom type and comment.
 * Example: window.penzaSubmitFortuneLead({ phone: '+7...', prize: 'Скидка до 50%' })
 */
async function penzaSubmitFortuneLead({ name = '', phone, prize = '', comment = '' } = {}) {
  await ensureBookingModal();
  const modal = document.getElementById('booking-modal');
  const form = modal?.querySelector('[data-lead-form]');
  if (!form) return Promise.reject(new Error('lead form not found'));

  const typeInput = form.querySelector('[data-lead-type]');
  const commentInput = form.querySelector('[data-lead-comment]');
  const pageUrlInput = form.querySelector('[data-page-url]');
  const nameInput = form.querySelector('[name="lead_name"]');
  const phoneInput = form.querySelector('[name="lead_phone"]');
  const consentInput = form.querySelector('[data-consent-checkbox]');

  if (typeInput) {
    typeInput.dataset.baseType = 'Колесо фортуны';
    typeInput.value = prize ? `Колесо фортуны — ${prize}` : 'Колесо фортуны';
  }
  if (commentInput) {
    const parts = [];
    if (prize) parts.push(`🎁 Заявка из колеса фортуны. Выпавший приз: ${prize}`);
    if (comment) parts.push(comment);
    commentInput.value = parts.join(' ');
  }
  if (pageUrlInput) pageUrlInput.value = window.location.href;
  if (nameInput) nameInput.value = name;
  if (phoneInput) phoneInput.value = phone;
  if (consentInput) {
    consentInput.checked = true;
    syncConsentSubmit(form);
  }

  syncLeadFormContext(form);
  form.requestSubmit();
  return Promise.resolve();
}

function initLeadForms() {
  document.querySelectorAll('[data-lead-form]').forEach((form) => {
    const typeInput = form.querySelector('[data-lead-type]');
    if (typeInput && !typeInput.dataset.baseType) {
      typeInput.dataset.baseType = typeInput.value || '';
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const consent = form.querySelector('[data-consent-checkbox]');
      if (consent && !consent.checked) return;

      syncLeadFormContext(form);

      const status = form.querySelector('.lead-form__status');
      const submit = form.querySelector('[type="submit"]');
      const submitLabel = submit?.textContent || 'Отправить заявку';
      if (submit) {
        submit.disabled = true;
        submit.textContent = 'Отправляем…';
      }
      if (status) {
        status.hidden = false;
        status.className = 'lead-form__status';
        status.textContent = '';
      }

      const data = new FormData(form);
      data.set('action', 'penza_lead_form');

      fetch(cfg.ajaxUrl || '/wp-admin/admin-ajax.php', {
        method: 'POST',
        body: data,
        credentials: 'same-origin',
      })
        .then((res) => res.json())
        .then(async (json) => {
          const modal = form.closest('#booking-modal');
          if (json.success) {
            form.reset();
            syncConsentSubmit(form);
            if (modal) {
              const mod = await ensureBookingModal();
              mod.showBookingModalSuccess(modal);
              return;
            }
            if (status) {
              status.hidden = false;
              status.classList.add('is-success');
              status.textContent = json.data?.message || 'Спасибо! Мы перезвоним вам.';
            }
          } else if (status) {
            status.hidden = false;
            status.classList.add('is-error');
            status.textContent = json.data?.message || 'Не удалось отправить.';
          }
        })
        .catch(() => {
          if (status) {
            status.classList.add('is-error');
            status.textContent = 'Не удалось отправить. Позвоните или напишите в WhatsApp.';
          }
        })
        .finally(() => {
          if (submit) {
            syncConsentSubmit(form);
            submit.textContent = submitLabel;
          }
        });
    });
  });
}

function initPriceAnchorBar() {
  const bar = document.querySelector('[data-price-anchor-bar]');
  if (!bar) return;

  const links = [...bar.querySelectorAll('.price-anchor-bar__link')];
  let barHeight = bar.offsetHeight;
  const headerH = () => {
    const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-offset'), 10);
    return Number.isFinite(offset) && offset > 0 ? offset : 72;
  };

  let scrollTicking = false;
  const onScroll = () => {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      bar.classList.toggle('is-scrolled', scrollY > 8);
      const scrollPos = scrollY + headerH() + barHeight + 12;
      let current = links[0];
      links.forEach((link) => {
        const id = link.getAttribute('href')?.replace('#', '');
        const target = id ? document.getElementById(id) : null;
        if (target && target.offsetTop <= scrollPos) current = link;
      });
      links.forEach((l) => l.classList.toggle('is-active', l === current));
      scrollTicking = false;
    });
  };

  window.addEventListener(
    'resize',
    () => {
      barHeight = bar.offsetHeight;
    },
    { passive: true }
  );

  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      const id = link.getAttribute('href')?.replace('#', '');
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - headerH() - bar.offsetHeight - 8;
      window.scrollTo({ top: top, behavior: 'smooth' });
    });
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

function initFaqAccordion() {
  document.querySelectorAll('[data-motion="faq-accordion"] .faq__item').forEach((item) => {
    const btn = item.querySelector('.faq__question');
    const answer = item.querySelector('.faq__answer');
    if (!btn || !answer) return;

    btn.addEventListener('click', () => {
      const open = item.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      answer.hidden = !open;
    });
  });
}

async function loadMotionBundle(page) {
  if (page === 'home') {
    const mod = await import(`${themeDist}/motion-home.js`);
    motionCleanup = mod.initHomeMotion();
  } else if (page === 'service' || page === 'blog') {
    const mod = await import(`${themeDist}/motion-lite.js`);
    motionCleanup = mod.initLiteMotion();
  }
}

function isBookingTriggerLike(el) {
  if (!el || el.closest('.booking-modal')) return false;
  if (el.matches(BOOKING_CLICK_SEL) || el.classList.contains('btn-book') || el.hasAttribute('data-booking')) {
    return true;
  }
  const text = (el.textContent || '').toLowerCase();
  if (!text.includes('запис')) return false;
  if (el.tagName === 'A') {
    const href = (el.getAttribute('href') || '').toLowerCase();
    if (href.startsWith('tel:') || href.includes('wa.me') || href.includes('whatsapp') || href === '#' || href === '') {
      return true;
    }
  }
  return el.tagName === 'BUTTON';
}

async function ensureBookingModal() {
  if (!bookingModule) {
    bookingModule = await import(`${themeDist}/booking-modal.js`);
  }
  if (!bookingInited) {
    bookingModule.initBookingModal();
    bookingInited = true;
  }
  return bookingModule;
}

function initBookingLazy() {
  const modal = document.getElementById('booking-modal');
  if (!modal) return;

  document.addEventListener(
    'click',
    async (e) => {
      if (bookingInited) return;
      const explicit = e.target.closest(BOOKING_CLICK_SEL);
      let trigger = explicit;
      if (!trigger) {
        const link = e.target.closest('a, button');
        if (link && isBookingTriggerLike(link)) trigger = link;
      }
      if (!trigger) return;

      e.preventDefault();
      e.stopPropagation();
      const mod = await ensureBookingModal();
      mod.openBookingModal(modal, trigger);
    },
    true
  );
}

function initFortuneWheelLazy() {
  if (!document.getElementById('fortune-wheel')) return;

  const boot = async () => {
    if (fortuneBooted) return window.penzaFortuneWheel;
    fortuneBooted = true;
    const mod = await import(`${themeDist}/fortune-wheel.js`);
    mod.initFortuneWheel(cfg);
    return window.penzaFortuneWheel;
  };

  const tab = document.getElementById('fortune-gift-tab');
  if (tab) {
    tab.addEventListener('click', async () => {
      const api = await boot();
      const modal = document.getElementById('fortune-wheel');
      if (api && modal?.hasAttribute('hidden')) {
        api.open();
      }
    });
  }
}

function initMotion() {
  const page = document.body.dataset.motionPage || cfg.motionPage || 'default';

  if (page === 'legal' || page === 'default' || page === 'blog-single') return;

  const run = () => {
    loadMotionBundle(page).catch((err) => {
      console.warn('[penza-motion] bundle load failed', err);
    });
  };

  const deferHeavy =
    page === 'home' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (deferHeavy) {
    afterLcp(() => {
      const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1200));
      idle(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(run);
        });
      });
    });
    return;
  }

  run();
}

window.addEventListener('pagehide', () => {
  if (typeof motionCleanup === 'function') motionCleanup();
});

window.penzaSubmitFortuneLead = penzaSubmitFortuneLead;

ready(() => {
  document.documentElement.classList.add('js-motion-ready');
  initCookieBanner();
  initMobileNav();
  initMegaNav();
  initStickyHeader();
  trackClicks();
  initPhoneMasks();
  initConsentGates();
  initLeadForms();
  initBookingLazy();
  initFortuneWheelLazy();
  initPriceAnchorBar();
  initFaqAccordion();
  initMotion();
});
